import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientService from '../services/clientService';
import { businessToday } from '../utils/dateFilters';

const toOption = (c) => ({
  value: c.id,
  label: `${c.firstName || ''} ${c.lastName || ''}`.trim() + (c.nifCin ? ` — ${c.nifCin}` : ''),
  data: c,
});
const round2 = (n) => Number((Number(n) || 0).toFixed(2));
const clamp0 = (n) => Math.max(0, Number(n) || 0);

function emptyValues() {
  return {
    confirmationCode: '', senderName: '', senderWhatsapp: '', senderPhone: '',
    client: null, beneficiaryName: '', receiverWhatsapp: '', receiverPhone: '',
    transactionDate: businessToday(), transactionReason: '', amountUSD: '', note: '',
    feeType: 'NONE', feePercent: '', feeFixedUSD: '',
  };
}

export default function ZelleModal({ show, onHide, onSave, initialData = null, currentRate = null }) {
  const init = useMemo(() => initialData ?? null, [initialData]);
  const isEdit = !!init?.id;
  const [selectKey, setSelectKey] = useState(0);
  const { register, handleSubmit, reset, control, watch, setValue, getValues, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: emptyValues(), shouldUnregister: true,
  });

  const selectedClient = watch('client');
  const feeType = watch('feeType');
  const amountUSD = Number(watch('amountUSD') || 0);
  const feePercent = Number(watch('feePercent') || 0);
  const feeFixedUSD = Number(watch('feeFixedUSD') || 0);

  const feeTotalUSD = useMemo(() => {
    const base = clamp0(amountUSD); const pct = clamp0(feePercent); const fixed = clamp0(feeFixedUSD);
    if (feeType === 'PERCENT') return round2(base * pct / 100);
    if (feeType === 'FIXED') return round2(fixed);
    if (feeType === 'MIXED') return round2(base * pct / 100 + fixed);
    return 0;
  }, [amountUSD, feePercent, feeFixedUSD, feeType]);
  const netUSD = useMemo(() => round2(Math.max(0, amountUSD - feeTotalUSD)), [amountUSD, feeTotalUSD]);
  const rateText = Number(currentRate) > 0 ? `1 USD = ${Number(currentRate).toFixed(2)} HTG` : '— (taux non défini)';

  const applyResetFromInit = useCallback(() => {
    reset({
      confirmationCode: init?.confirmationCode || '',
      senderName: init?.senderName || '', senderWhatsapp: init?.senderWhatsapp || '', senderPhone: init?.senderPhone || '',
      client: init?.client ? toOption(init.client) : null,
      beneficiaryName: init?.beneficiaryName || '', receiverWhatsapp: init?.receiverWhatsapp || '', receiverPhone: init?.receiverPhone || '',
      transactionDate: init?.transactionDate || businessToday(), transactionReason: init?.transactionReason || '',
      amountUSD: init?.amountUSD ?? '', note: init?.note || '',
      feeType: init?.feeType ? String(init.feeType).toUpperCase() : 'NONE',
      feePercent: init?.feePercent != null ? String(init.feePercent) : '',
      feeFixedUSD: init?.feeFixedUSD != null ? String(init.feeFixedUSD) : '',
    });
  }, [init, reset]);

  useEffect(() => {
    if (!show) return;
    applyResetFromInit();
    setSelectKey((k) => k + 1);
  }, [show, applyResetFromInit]);

  useEffect(() => {
    if (!show) return;
    const c = selectedClient?.data;
    if (!c) return;
    const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    if (fullName && String(getValues('beneficiaryName') || '').trim() !== fullName) {
      setValue('beneficiaryName', fullName);
    }
  }, [show, selectedClient, getValues, setValue]);

  const loadClientOptions = async (inputValue, loadedOptions, { page }) => {
    const res = await clientService.searchPaged({ q: inputValue || '', page, pageSize: 20 });
    return {
      options: (res?.rows || []).map(toOption),
      hasMore: page < (res?.totalPages || 1),
      additional: { page: page + 1 },
    };
  };

  const handleClose = () => { reset(emptyValues()); setSelectKey((k) => k + 1); onHide(); };
  const submit = (vals) => {
    const ft = String(vals.feeType || 'NONE').toUpperCase();
    const feeTypePayload = ft === 'NONE' ? 'NONE' : ft;
    const amountValue = vals.amountUSD === '' || vals.amountUSD == null ? null : Number(vals.amountUSD);
    if (amountValue != null && (!Number.isFinite(amountValue) || amountValue <= 0)) {
      setError('amountUSD', { type: 'validate', message: 'Doit être > 0 ou laissé vide' }); return;
    }
    if (amountValue != null && feeTotalUSD > amountValue + 1e-9) {
      setError('feeType', { type: 'validate', message: 'Les frais ne peuvent pas dépasser le montant USD.' }); return;
    }
    clearErrors(['amountUSD', 'feeType']);
    onSave({
      confirmationCode: String(vals.confirmationCode || '').trim(),
      senderName: String(vals.senderName || '').trim(),
      senderWhatsapp: String(vals.senderWhatsapp || '').trim() || null,
      senderPhone: String(vals.senderPhone || '').trim() || null,
      clientId: vals.client?.value ?? null,
      beneficiaryName: String(vals.beneficiaryName || '').trim() || null,
      receiverWhatsapp: String(vals.receiverWhatsapp || '').trim() || null,
      receiverPhone: String(vals.receiverPhone || '').trim() || null,
      transactionDate: vals.transactionDate || businessToday(),
      transactionReason: String(vals.transactionReason || '').trim() || null,
      amountUSD: amountValue,
      note: String(vals.note || '').trim() || null,
      feeType: feeTypePayload,
      feePercent: ['PERCENT', 'MIXED'].includes(feeTypePayload) ? Number(vals.feePercent || 0) : 0,
      feeFixedUSD: ['FIXED', 'MIXED'].includes(feeTypePayload) ? Number(vals.feeFixedUSD || 0) : 0,
    });
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="lg">
      <Modal.Header closeButton><Modal.Title>{isEdit ? 'Modifier Zelle' : 'Nouvelle Zelle'}</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          <Alert variant="secondary"><strong>Taux du jour :</strong> {rateText}</Alert>

          <h6>Sender</h6>
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label>Nom *</Form.Label><Form.Control {...register('senderName', { required: 'Requis' })} />{errors.senderName && <Form.Text className="text-danger">{errors.senderName.message}</Form.Text>}</Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Tel (WhatsApp)</Form.Label><Form.Control {...register('senderWhatsapp')} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Tel (Appel)</Form.Label><Form.Control {...register('senderPhone')} /></Form.Group></Col>
          </Row>

          <hr className="my-4" />
          <h6>Receiver</h6>
          <Form.Group className="mb-3">
            <Form.Label>Déjà client ?</Form.Label>
            <Controller control={control} name="client" render={({ field }) => (
              <AsyncPaginate key={selectKey} value={field.value} onChange={field.onChange} loadOptions={loadClientOptions} additional={{ page: 1 }} debounceTimeout={300} placeholder="Sélectionner un client (facultatif)…" isClearable />
            )} />
          </Form.Group>
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label>Nom</Form.Label><Form.Control disabled={!!selectedClient?.value} {...register('beneficiaryName')} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Tel (WhatsApp)</Form.Label><Form.Control {...register('receiverWhatsapp')} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Tel (Appel)</Form.Label><Form.Control {...register('receiverPhone')} /></Form.Group></Col>
          </Row>

          <hr className="my-4" />
          <h6>Infos de la transaction</h6>
          <Row className="g-3">
            <Col md={4}><Form.Group><Form.Label>Date</Form.Label><Form.Control type="date" {...register('transactionDate')} /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Code confirmation *</Form.Label><Form.Control {...register('confirmationCode', { required: 'Requis' })} />{errors.confirmationCode && <Form.Text className="text-danger">{errors.confirmationCode.message}</Form.Text>}</Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Montant reçu (USD)</Form.Label><Form.Control type="number" step="0.01" {...register('amountUSD')} />{errors.amountUSD && <Form.Text className="text-danger">{errors.amountUSD.message}</Form.Text>}</Form.Group></Col>
            <Col md={12}><Form.Group><Form.Label>Motif de la transaction</Form.Label><Form.Control {...register('transactionReason')} /></Form.Group></Col>
          </Row>

          <hr className="my-4" />
          <h6>Frais</h6>
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label>Type de frais</Form.Label><Form.Select {...register('feeType')}><option value="NONE">Aucun</option><option value="PERCENT">Pourcentage (%)</option><option value="FIXED">Montant fixe (USD)</option><option value="MIXED">Mixte (% + fixe)</option></Form.Select>{errors.feeType && <Form.Text className="text-danger">{errors.feeType.message}</Form.Text>}</Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>%</Form.Label><Form.Control type="number" step="0.01" disabled={!['PERCENT', 'MIXED'].includes(feeType)} {...register('feePercent')} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Fixe (USD)</Form.Label><Form.Control type="number" step="0.01" disabled={!['FIXED', 'MIXED'].includes(feeType)} {...register('feeFixedUSD')} /></Form.Group></Col>
          </Row>
          <Alert variant="light" className="mt-3 mb-0">
            <div className="d-flex justify-content-between"><span>Montant reçu</span><strong>{round2(amountUSD).toFixed(2)} USD</strong></div>
            <div className="d-flex justify-content-between"><span>Frais</span><strong>{feeTotalUSD.toFixed(2)} USD</strong></div>
            <div className="d-flex justify-content-between"><span>Net à remettre</span><strong>{netUSD.toFixed(2)} USD</strong></div>
          </Alert>
          <Form.Group className="mt-3"><Form.Label>Note</Form.Label><Form.Control as="textarea" rows={2} {...register('note')} /></Form.Group>
          <Form.Text className="text-muted">Seuls le nom du Sender et le code de confirmation sont obligatoires à la création. Le montant doit être complété avant le paiement.</Form.Text>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={handleClose}>Annuler</Button><Button type="submit">{isEdit ? 'Enregistrer' : 'Créer'}</Button></Modal.Footer>
      </Form>
    </Modal>
  );
}
